import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowRight, Copy, Check, ExternalLink, TrendingUp, ShieldCheck, Zap } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------- shared visual helpers ----------
const scoreTone = (n) => (n >= 70 ? "emerald" : n >= 40 ? "amber" : "rose");
const toneClasses = {
  emerald: { bar: "bg-emerald-500", text: "text-emerald-700", ring: "ring-emerald-500/30", chip: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  amber:   { bar: "bg-amber-500",   text: "text-amber-700",   ring: "ring-amber-500/30",   chip: "bg-amber-50 text-amber-800 border-amber-200" },
  rose:    { bar: "bg-rose-500",    text: "text-rose-700",    ring: "ring-rose-500/30",    chip: "bg-rose-50 text-rose-800 border-rose-200" },
};

export const AuditResult = ({ result, shareUrl, showShare = true }) => {
  const [copied, setCopied] = useState(false);
  if (!result) return null;
  const overall = result.overall_score ?? 0;
  const t = toneClasses[scoreTone(overall)];

  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Shareable audit link copied.");
      setTimeout(() => setCopied(false), 2500);
    } catch { toast.error("Copy failed — highlight and copy manually."); }
  };

  return (
    <div className="space-y-8" data-testid="leadgen-audit-result">
      {/* Overall score hero */}
      <Card className="p-6 sm:p-8 border border-slate-200 rounded-2xl">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-8">
          <div className={`flex-shrink-0 relative w-40 h-40 rounded-full ring-8 ${t.ring} flex items-center justify-center bg-white shadow-sm`}>
            <div className="text-center">
              <div className={`text-5xl font-black ${t.text} leading-none`} data-testid="audit-overall-score">{overall}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mt-1">/ 100</div>
            </div>
          </div>
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Lead Generation Score</div>
            <h2 className="text-3xl font-black text-slate-900 mt-1">{result.band || (overall >= 70 ? "Strong" : overall >= 40 ? "Needs Work" : "Critical")}</h2>
            <p className="text-slate-700 mt-2 max-w-2xl">{result.summary || "Your audit is ready. Review the sub-scores and the prioritized fixes below."}</p>
            <div className="mt-3 text-sm text-slate-500">Analyzed <span className="font-mono text-slate-700">{result.final_url || result.url}</span></div>
            {showShare && shareUrl && (
              <div className="mt-4 flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={doCopy} className="rounded-full" data-testid="audit-copy-share">
                  {copied ? <><Check size={14} className="mr-1.5" /> Copied</> : <><Copy size={14} className="mr-1.5" /> Copy shareable link</>}
                </Button>
                <a href={shareUrl} target="_blank" rel="noreferrer" className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1">Open <ExternalLink size={12} /></a>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* 7 subscores */}
      <div>
        <h3 className="text-xl font-bold text-slate-900 mb-4">Sub-scores</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(result.subscores || []).map((s) => {
            const st = toneClasses[scoreTone(s.score)];
            return (
              <Card key={s.key} className="p-4 border border-slate-200 rounded-xl" data-testid={`audit-subscore-${s.key}`}>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-slate-900">{s.label}</span>
                    {!s.measured && <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">AI-estimated</span>}
                  </div>
                  <span className={`text-2xl font-black ${st.text}`}>{s.score}</span>
                </div>
                <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                  <div className={`h-full ${st.bar} rounded-full transition-all`} style={{ width: `${s.score}%` }} />
                </div>
                <div className="text-xs text-slate-500 mt-2">{s.blurb}</div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Prioritized fixes */}
      <div>
        <h3 className="text-xl font-bold text-slate-900 mb-4">Prioritized fixes</h3>
        <div className="space-y-3">
          {(result.fixes || []).map((f, i) => (
            <Card key={i} className="p-5 border border-slate-200 rounded-xl" data-testid={`audit-fix-${i}`}>
              <div className="flex items-start gap-4">
                <div className="text-3xl font-black text-slate-200 leading-none w-10">{String(i + 1).padStart(2, "0")}</div>
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="font-bold text-slate-900 flex-1">{f.title}</div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide border ${toneClasses[f.impact === "high" ? "emerald" : f.impact === "medium" ? "amber" : "rose"].chip}`}>Impact · {f.impact}</span>
                  </div>
                  <div className="text-sm text-slate-600 mt-2">{f.why}</div>
                  {f.url && (
                    <Link to={f.url} className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-blue-700 hover:text-blue-900">
                      Explore this WebsiteTech service <ArrowRight size={14} />
                    </Link>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* CTAs */}
      <Card className="p-6 border border-slate-200 rounded-2xl bg-slate-50/50">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Link to="/growth-advisor" className="flex flex-col items-start gap-1 p-4 rounded-xl bg-white border border-slate-200 hover:border-slate-400 transition-colors">
            <TrendingUp size={20} className="text-blue-700 mb-1" />
            <div className="font-bold text-slate-900">Get a growth plan</div>
            <div className="text-xs text-slate-500">Personalized service + package recommendation.</div>
          </Link>
          <Link to="/packages" className="flex flex-col items-start gap-1 p-4 rounded-xl bg-white border border-slate-200 hover:border-slate-400 transition-colors">
            <ShieldCheck size={20} className="text-emerald-700 mb-1" />
            <div className="font-bold text-slate-900">See packages</div>
            <div className="text-xs text-slate-500">START · GROW · SCALE · PERFORMANCE.</div>
          </Link>
          <Link to="/lead-generation/laas" className="flex flex-col items-start gap-1 p-4 rounded-xl bg-white border border-slate-200 hover:border-slate-400 transition-colors">
            <Zap size={20} className="text-amber-700 mb-1" />
            <div className="font-bold text-slate-900">Request a proposal</div>
            <div className="text-xs text-slate-500">Managed Lead Generation as a Service.</div>
          </Link>
        </div>
      </Card>
    </div>
  );
};

// ---------- main page ----------
export default function LeadGenAuditTool() {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [shareUrl, setShareUrl] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Lead Generation Audit | WebsiteTech.pro";
  }, []);

  const run = async (e) => {
    e?.preventDefault();
    const clean = (url || "").trim();
    if (!clean) { toast.error("Paste a website URL first."); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await axios.post(`${API}/leadgen/audit`, {
        url: clean, source: "leadgen-audit-tool",
      });
      setResult(data);
      const link = data.share_token
        ? `${window.location.origin}/lead-generation/audit/${data.share_token}`
        : "";
      setShareUrl(link);
      setTimeout(() => document.getElementById("audit-result")?.scrollIntoView({ behavior: "smooth" }), 200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Audit failed — please try a different URL.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-200 text-blue-800 text-[11px] font-bold uppercase tracking-widest">Lead Generation Audit</div>
          <h1 className="text-4xl sm:text-5xl font-black text-slate-900 mt-4 max-w-3xl mx-auto">See exactly why you&apos;re not getting more leads.</h1>
          <p className="text-slate-600 mt-4 max-w-2xl mx-auto">Paste any business URL. In 20-40 seconds our AI scores your entire acquisition system across 7 dimensions and returns prioritized fixes tied to real WebsiteTech services.</p>
        </div>

        <form onSubmit={run} className="max-w-2xl mx-auto flex flex-col sm:flex-row gap-3" data-testid="leadgen-audit-form">
          <Input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://your-business.com"
            className="flex-1 h-12 text-base"
            data-testid="leadgen-audit-url"
            required
          />
          <Button type="submit" disabled={loading} className="h-12 px-6 bg-slate-900 hover:bg-slate-800 rounded-full sm:min-w-[180px]" data-testid="leadgen-audit-run">
            {loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Analyzing…</> : "Run my audit"}
          </Button>
        </form>
        <div className="text-center text-xs text-slate-500 mt-3">No signup required · Shareable results · One 0-100 score + 7 sub-scores</div>

        {result && (
          <div id="audit-result" className="mt-12">
            <AuditResult result={result} shareUrl={shareUrl} />
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
